import React, { useState } from 'react';
import { Button } from '@mui/material';

// Restricted Markdown subset (OWASP LLM05): no auto-loaded images, no raw HTML,
// links show the full URL with rel="noopener noreferrer", code is displayed never executed.
const CODE_FENCE_RE = /```([\s\S]*?)```/g;
// URL part allows one level of nested parens (e.g. "javascript:alert(1)") so the
// outer link/image closing paren is never mistaken for an inner one.
const INLINE_TOKEN_RE = /(`[^`\n]+`)|(!\[[^\]]*\]\((?:[^()]|\([^()]*\))*\))|(\[[^\]]*\]\((?:[^()]|\([^()]*\))*\))/g;
const IMAGE_RE = /^!\[([^\]]*)\]\(((?:[^()]|\([^()]*\))*)\)$/;
const LINK_RE = /^\[([^\]]*)\]\(((?:[^()]|\([^()]*\))*)\)$/;

// Single chokepoint every rendered href must pass through: only http/https survive.
export function resolveSafeHref(url) {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  const match = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(trimmed);
  if (!match) return null;
  const scheme = match[1].toLowerCase();
  if (scheme !== 'http' && scheme !== 'https') return null;
  return trimmed;
}

function stripCodeFenceLanguage(raw) {
  const newlineIdx = raw.indexOf('\n');
  if (newlineIdx === -1) return raw;
  const firstLine = raw.slice(0, newlineIdx);
  if (/^[\w-]*$/.test(firstLine.trim())) {
    return raw.slice(newlineIdx + 1).replace(/\n$/, '');
  }
  return raw.replace(/\n$/, '');
}

function ImagePlaceholder({ alt, url }) {
  const [loaded, setLoaded] = useState(false);
  if (loaded) {
    return <img src={url} alt={alt} />;
  }
  return (
    <Button
      size="small"
      variant="text"
      className="restricted-markdown-image-placeholder"
      onClick={() => setLoaded(true)}
      sx={{ textTransform: 'none' }}
    >
      {`[image: ${alt || url}] ${url}`}
    </Button>
  );
}

function renderInline(text, keyPrefix) {
  const nodes = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  INLINE_TOKEN_RE.lastIndex = 0;
  while ((match = INLINE_TOKEN_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (match[1]) {
      nodes.push(<code key={`${keyPrefix}-${key++}`}>{token.slice(1, -1)}</code>);
    } else if (match[2]) {
      const imgMatch = IMAGE_RE.exec(token);
      const alt = imgMatch ? imgMatch[1] : '';
      const url = imgMatch ? imgMatch[2] : '';
      nodes.push(<ImagePlaceholder key={`${keyPrefix}-${key++}`} alt={alt} url={url} />);
    } else if (match[3]) {
      const linkMatch = LINK_RE.exec(token);
      const linkText = linkMatch ? linkMatch[1] : token;
      const url = linkMatch ? linkMatch[2] : '';
      const safeHref = resolveSafeHref(url);
      if (safeHref) {
        nodes.push(
          <a key={`${keyPrefix}-${key++}`} href={safeHref} target="_blank" rel="noopener noreferrer">
            {safeHref}
          </a>
        );
      } else {
        nodes.push(linkText);
      }
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

export default function RestrictedMarkdown({ content }) {
  if (typeof content !== 'string' || content.length === 0) {
    return null;
  }

  const blocks = [];
  let lastIndex = 0;
  let match;
  let blockKey = 0;
  CODE_FENCE_RE.lastIndex = 0;
  while ((match = CODE_FENCE_RE.exec(content)) !== null) {
    if (match.index > lastIndex) {
      blocks.push({ type: 'text', value: content.slice(lastIndex, match.index), key: blockKey++ });
    }
    blocks.push({ type: 'code', value: stripCodeFenceLanguage(match[1]), key: blockKey++ });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    blocks.push({ type: 'text', value: content.slice(lastIndex), key: blockKey++ });
  }

  return (
    <div className="restricted-markdown">
      {blocks.map((block) => {
        if (block.type === 'code') {
          return (
            <pre key={block.key}>
              <code>{block.value}</code>
            </pre>
          );
        }
        return <p key={block.key}>{renderInline(block.value, block.key)}</p>;
      })}
    </div>
  );
}
